import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';

// ─── Utilities ─────────────────────────────────────────────────────────────────

function getTodayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getInitialDate() {
  const p = new URLSearchParams(window.location.search);
  const d = p.get('date');
  if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return getTodayYmd();
}

function isWeekendDate(dateStr) {
  if (!dateStr) return false;
  const day = new Date(`${dateStr}T00:00:00`).getDay();
  return day === 0 || day === 6;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function toMins(t) {
  if (!t) return 0;
  const [h = 0, m = 0] = String(t).split(':').map(Number);
  return h * 60 + m;
}

function fmtTime(t) {
  if (!t) return '—';
  return String(t).slice(0, 5);
}

function durationStr(start, end) {
  const d = toMins(end) - toMins(start);
  if (d <= 0) return '—';
  const h = Math.floor(d / 60);
  const m = d % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function totalHoursFor(rows) {
  const mins = rows.reduce((s, r) => s + Math.max(0, toMins(r.endTime) - toMins(r.startTime)), 0);
  return (mins / 60).toFixed(1);
}

// Extract scheduleType from a batch object (field is nested in batch.course)
function getBatchScheduleType(b) {
  return b?.course?.scheduleType || b?.scheduleType || null;
}

// Extract course full name from a batch object
function getBatchCourseName(b) {
  return b?.course?.fullName || b?.courseFullName || b?.course?.shortName || b?.courseShortName || '';
}

// Extract course short name from a batch object
function getBatchCourseShortName(b) {
  return b?.course?.shortName || b?.courseShortName || '';
}

// ─── Lecturer availability utilities ──────────────────────────────────────────

const WORK_START = 8 * 60 + 30;   // 8:30 AM in minutes
const WORK_END   = 17 * 60;        // 5:00 PM in minutes
const HALF_MID   = 12 * 60 + 30;  // 12:30 PM in minutes

function getWorkingWindow(roster) {
  if (!roster) return [{ start: WORK_START, end: WORK_END }];
  if (roster.dayType === 'off day') return [];
  if (roster.dayType === 'morning half day') return [{ start: WORK_START, end: HALF_MID }];
  if (roster.dayType === 'evening half day') return [{ start: HALF_MID, end: WORK_END }];
  return [{ start: WORK_START, end: WORK_END }];
}

function subtractBusySlots(windows, busy) {
  let free = [...windows];
  busy.forEach(({ start: bs, end: be }) => {
    const next = [];
    free.forEach(({ start: ws, end: we }) => {
      if (be <= ws || bs >= we) { next.push({ start: ws, end: we }); }
      else {
        if (bs > ws) next.push({ start: ws, end: bs });
        if (be < we) next.push({ start: be, end: we });
      }
    });
    free = next;
  });
  return free.filter((s) => s.end > s.start);
}

function minsToDisplay(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
}

function slotDur(slot) {
  const d = slot.end - slot.start;
  if (d <= 0) return '';
  const h = Math.floor(d / 60);
  const m = d % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function totalSlotDur(slots) {
  const d = slots.reduce((sum, s) => sum + Math.max(0, s.end - s.start), 0);
  if (d <= 0) return '';
  const h = Math.floor(d / 60);
  const m = d % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m total`;
  if (h > 0) return `${h}h total`;
  return `${m}m total`;
}

function StaffAvatar({ staff }) {
  const [error, setError] = useState(false);
  const initials = (staff.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (!error && staff.imagePath) {
    return (
      <img
        src={getImageURL(staff.imagePath)}
        alt={staff.name}
        onError={() => setError(true)}
        className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
      <span className="text-[10px] font-bold text-gray-500 leading-none">{initials}</span>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const STAT_COLORS = {
  green:  'bg-green-50  border-green-100  text-green-700',
  orange: 'bg-orange-50 border-orange-100 text-orange-700',
  blue:   'bg-blue-50   border-blue-100   text-blue-700',
  indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
  purple: 'bg-purple-50 border-purple-100 text-purple-700',
  teal:   'bg-teal-50   border-teal-100   text-teal-700',
};

function StatCard({ label, value, sub, color = 'indigo' }) {
  return (
    <div className={`rounded-xl border p-4 ${STAT_COLORS[color] || STAT_COLORS.indigo}`}>
      <div className="text-2xl font-bold leading-tight">{value}</div>
      <div className="text-xs font-medium mt-1 opacity-80">{label}</div>
      {sub && <div className="text-[10px] mt-0.5 opacity-60">{sub}</div>}
    </div>
  );
}

function SectionTitle({ iconPath, title, count }) {
  return (
    <div className="flex items-center gap-2 mb-4 print-section-title">
      <div className="w-6 h-6 bg-indigo-100 rounded flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </div>
      <h2 className="text-sm font-bold text-gray-800 flex-1">{title}</h2>
      {count != null && (
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
      )}
    </div>
  );
}

// ─── Print styles ─────────────────────────────────────────────────────────────

const PRINT_STYLES = `
@media print {
  @page { size: A4 portrait; margin: 18mm 14mm; }

  body * { visibility: hidden; }
  #daily-report-print, #daily-report-print * { visibility: visible; }

  #daily-report-print {
    position: absolute; left: 0; top: 0; width: 100%;
    background: #fff; padding: 0; margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9pt; color: #111; line-height: 1.5;
  }

  .print-show { display: block !important; }
  .print-hide { display: none !important; }

  #daily-report-print * {
    box-shadow: none !important; border-radius: 0 !important;
    animation: none !important; transition: none !important;
  }
  #daily-report-print span, #daily-report-print p,
  #daily-report-print li, #daily-report-print div,
  #daily-report-print td, #daily-report-print th {
    color: #111 !important; background-color: transparent !important;
  }

  .print-doc-header { border-bottom: 1.5pt solid #333; padding-bottom: 6pt; margin-bottom: 10pt; }
  .print-doc-header h1 { font-size: 14pt; font-weight: bold; margin: 0; color: #111 !important; }
  .print-doc-header p  { font-size: 9pt; margin: 2pt 0 0; color: #444 !important; }

  #daily-report-print .print-stats-grid {
    display: grid !important; grid-template-columns: repeat(6, 1fr) !important;
    gap: 0 !important; border: 0.75pt solid #aaa !important; margin-bottom: 12pt !important;
  }
  #daily-report-print .print-stats-grid > div {
    border-right: 0.75pt solid #aaa !important; padding: 4pt 5pt !important;
    text-align: center !important; background: transparent !important;
  }
  #daily-report-print .print-stats-grid > div:last-child { border-right: none !important; }

  #daily-report-print .print-section-title { display: flex !important; align-items: center; margin-bottom: 6pt; }
  #daily-report-print .print-section-title > div:first-child { display: none !important; }
  #daily-report-print .print-section-title h2 {
    font-size: 10.5pt !important; font-weight: bold !important;
    color: #111 !important; border-bottom: 1pt solid #333 !important;
    padding-bottom: 2pt !important; flex: 1;
  }

  #daily-report-print .print-course-block {
    border: 0.5pt solid #bbb !important; margin-bottom: 5pt !important;
    page-break-inside: avoid; break-inside: avoid;
  }
  #daily-report-print .print-course-hdr {
    background: #ebebeb !important; border-bottom: 0.5pt solid #bbb !important; padding: 3pt 6pt !important;
  }
  #daily-report-print .print-course-hdr * { color: #111 !important; background: transparent !important; }
  #daily-report-print .print-no-class  { border-left: 3pt solid #b7791f !important; }
  #daily-report-print .print-low-hours { border-left: 3pt solid #c53030 !important; }

  #daily-report-print table { border-collapse: collapse !important; font-size: 8pt !important; }
  #daily-report-print th, #daily-report-print td {
    border: 0.4pt solid #ccc !important; padding: 1.5pt 4pt !important;
    color: #111 !important; background: transparent !important;
  }
  #daily-report-print thead th { background: #f0f0f0 !important; font-weight: bold !important; }

  #daily-report-print .print-avail-grid {
    display: grid !important; grid-template-columns: 1fr 1fr !important;
    gap: 8pt !important; align-items: start !important;
  }

  #daily-report-print .print-type-hdr {
    background: #e0e0e0 !important; border: 0.5pt solid #999 !important;
    padding: 2pt 5pt !important; margin-bottom: 3pt !important;
  }
  #daily-report-print .print-type-hdr * { color: #111 !important; background: transparent !important; }

  #daily-report-print .print-panel {
    border: 0.5pt solid #ccc !important; margin-bottom: 4pt !important;
    page-break-inside: avoid; break-inside: avoid;
  }
  #daily-report-print .print-panel-hdr {
    background: #f0f0f0 !important; border-bottom: 0.5pt solid #ccc !important; padding: 2pt 5pt !important;
  }
  #daily-report-print .print-panel-hdr * { color: #111 !important; background: transparent !important; }
}

@media screen {
  .print-show { display: none !important; }
}
`;

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function DailyClassScheduleReport() {
  const navigate = useNavigate();

  const _authUser = AuthService.getUser();
  const loggedName = _authUser?.superAdmin?.staff?.name || _authUser?.superAdmin?.username || 'Staff';
  const loggedPosition = _authUser?.superAdmin?.positionName || _authUser?.superAdmin?.staffType || '';

  const [date, setDate] = useState(getInitialDate);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAssignmentRooms, setSelectedAssignmentRooms] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);

  // Raw API data
  const [schedules, setSchedules] = useState([]);   // rows from /admin/attendance/schedules
  const [batches, setBatches] = useState([]);        // from /admin/batches — each has batch.course.scheduleType
  const [staffSchedules, setStaffSchedules] = useState([]); // from /admin/staff-schedules
  const [allClassrooms, setAllClassrooms] = useState([]); // from /admin/classrooms/list
  const [unavailability, setUnavailability] = useState([]); // from /admin/staff-unavailability → data.records

  // Keep URL in sync
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('date', date);
    window.history.replaceState({}, '', url.toString());
  }, [date]);

  // Fetch all data whenever date changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      axiosInstance.get('/admin/attendance/schedules', { params: { date } }),
      axiosInstance.get('/admin/batches', {
        params: { status: 'ongoing' },
        headers: { 'x-accessible-courses': 'all' },
      }),
      axiosInstance.get('/admin/staff-schedules', { params: { date } }),
      axiosInstance.get('/admin/classrooms/list'),
      axiosInstance.get('/admin/staff-unavailability', {
        params: { dateFrom: date, dateTo: date },
      }),
    ])
      .then(([schRes, batchRes, staffRes, roomRes, unavailRes]) => {
        if (cancelled) return;

        // Schedules — API returns array directly
        setSchedules(Array.isArray(schRes.data) ? schRes.data : []);

        // Batches — wrapped as { batches: [...], courses: [...], stats: {}, filters: {} }
        const bd = batchRes.data;
        setBatches(Array.isArray(bd) ? bd : (Array.isArray(bd?.batches) ? bd.batches : []));

        // Staff schedules — API returns array directly
        setStaffSchedules(Array.isArray(staffRes.data) ? staffRes.data : []);

        // Classrooms — API returns array directly
        const rd = roomRes.data;
        setAllClassrooms(Array.isArray(rd) ? rd : (Array.isArray(rd?.classrooms) ? rd.classrooms : []));

        // Staff unavailability — wrapped as { records: [...] }
        const ud = unavailRes.data;
        setUnavailability(Array.isArray(ud) ? ud : (Array.isArray(ud?.records) ? ud.records : []));

        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('DailyClassScheduleReport fetch error:', err);
          setError('Failed to load report data. Please refresh and try again.');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [date]);

  // ── Derived data ─────────────────────────────────────────────────────────────

  const dayType = isWeekendDate(date) ? 'weekend' : 'weekday';

  // Build course groups from ongoing batches (filter by matching scheduleType).
  // Each batch.course.scheduleType is 'weekday' or 'weekend'.
  // Merge with schedule rows so batches without classes still appear.
  const courseGroups = useMemo(() => {
    const typedBatches = batches.filter((b) => getBatchScheduleType(b) === dayType);

    const map = {};
    typedBatches.forEach((b) => {
      const courseName = getBatchCourseName(b);
      const key = courseName || String(b.batchId);
      if (!map[key]) {
        map[key] = {
          courseName,
          courseShortName: getBatchCourseShortName(b),
          batches: [],
        };
      }
      // Match schedule rows by batchId (both are numbers from the API)
      const batchId = Number(b.batchId);
      const batchSch = schedules
        .filter((s) => Number(s.batchId) === batchId)
        .sort((a, c) => toMins(a.startTime) - toMins(c.startTime));

      map[key].batches.push({
        batchId,
        batchName: b.batchName || '',
        batchInternalName: b.batchInternalName || b.batchName || '',
        whatsappGroupName: b.whatsappGroupName || '',
        schedules: batchSch,
        totalHours: totalHoursFor(batchSch),
      });
    });

    // Sort courses alphabetically, batches within each course alphabetically
    return Object.values(map)
      .sort((a, b) => a.courseName.localeCompare(b.courseName))
      .map((c) => ({
        ...c,
        batches: c.batches.sort((a, b) => a.batchInternalName.localeCompare(b.batchInternalName)),
      }));
  }, [batches, schedules, dayType]);

  // Build staff availability panels grouped by staff.type (academic / sales / etc.)
  const lecturerPanels = useMemo(() => {
    const unavailByStaff = {};
    unavailability.forEach((u) => {
      const id = Number(u.staffId);
      if (!unavailByStaff[id]) unavailByStaff[id] = [];
      unavailByStaff[id].push(u);
    });

    const groups = {};

    staffSchedules.forEach((staff) => {
      const isVisiting = /visiting/i.test(staff.positionName || '') || /visiting/i.test(staff.type || '');
      const typeKey = isVisiting ? 'visiting lecturer' : (staff.category || 'other').toLowerCase().trim();
      if (!groups[typeKey]) groups[typeKey] = { withClasses: [], free: [], onLeave: [], offDay: [] };
      const g = groups[typeKey];

      const workWindow = getWorkingWindow(staff.roster);
      const staffUnavail = unavailByStaff[Number(staff.staffId)] || [];
      const hasFullDayLeave = staffUnavail.some((u) => u.type === 'leave');

      if (workWindow.length === 0) {
        g.offDay.push(staff);
        return;
      }

      if (hasFullDayLeave) {
        g.onLeave.push({ staff, unavail: staffUnavail });
        return;
      }

      const staffEvents = staff.events || [];

      const busySlots = [
        ...staff.schedules.map((s) => ({ start: toMins(s.startTime), end: toMins(s.endTime) })),
        ...staffEvents
          .filter((e) => e.startTime && e.endTime)
          .map((e) => ({ start: toMins(e.startTime), end: toMins(e.endTime) })),
        ...staffUnavail
          .filter((u) => u.type === 'unavailability' && u.startTime && u.endTime)
          .map((u) => ({ start: toMins(u.startTime), end: toMins(u.endTime) })),
      ].sort((a, b) => a.start - b.start);

      const freeSlots = subtractBusySlots(workWindow, busySlots);

      // Deduplicate: same time + subject + room = merged multi-batch class, show once
      const seenKeys = new Set();
      const displaySchedules = [...staff.schedules]
        .sort((a, b) => toMins(a.startTime) - toMins(b.startTime))
        .filter((s) => {
          const key = `${s.startTime}|${s.endTime}|${s.subjectName}|${s.classroom}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

      const displayEvents = [...staffEvents].sort((a, b) => toMins(a.startTime) - toMins(b.startTime));

      if (staff.schedules.length > 0 || staffEvents.length > 0) {
        g.withClasses.push({ staff, freeSlots, unavail: staffUnavail, displaySchedules, displayEvents });
      } else {
        g.free.push({ staff, freeSlots, unavail: staffUnavail, displaySchedules, displayEvents });
      }
    });

    const byName = (a, b) => (a.staff?.name || a.name || '').localeCompare(b.staff?.name || b.name || '');
    Object.values(groups).forEach((g) => {
      g.withClasses.sort(byName);
      g.free.sort(byName);
      g.onLeave.sort(byName);
      g.offDay.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    return groups; // { academic: {...}, sales: {...}, ... }
  }, [staffSchedules, unavailability]);

  // Classrooms in use today (from schedule rows)
  const scheduledClassroomIds = useMemo(
    () => new Set(schedules.map((s) => Number(s.classroomId)).filter(Boolean)),
    [schedules]
  );

  const occupiedClassrooms = useMemo(() => {
    const map = {};
    schedules.forEach((s) => {
      if (!s.classroomId) return;
      const id = Number(s.classroomId);
      if (!map[id]) {
        const roomInfo = allClassrooms.find((r) => Number(r.classroomId) === id);
        map[id] = {
          classroomId: id,
          name: s.classroomName || roomInfo?.name || '—',
          floor: roomInfo?.floor ?? '',
          sessions: [],
        };
      }
      map[id].sessions.push(s);
    });

    return Object.values(map)
      .map((room) => {
        const sorted = [...room.sessions].sort((a, b) => toMins(a.startTime) - toMins(b.startTime));
        // Deduplicate: same time + subject + lecturer = merged multi-batch session
        const seenKeys = new Set();
        const displaySessions = sorted.filter((s) => {
          const key = `${s.startTime}|${s.endTime}|${s.subjectShortName || s.subjectFullName}|${s.staffName}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });
        const busySlots = displaySessions.map((s) => ({ start: toMins(s.startTime), end: toMins(s.endTime) }));
        return {
          ...room,
          displaySessions,
          totalHours: totalHoursFor(displaySessions),
          freeSlots: subtractBusySlots([{ start: WORK_START, end: WORK_END }], busySlots),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [schedules, allClassrooms]);

  const freeClassrooms = useMemo(
    () =>
      allClassrooms
        .filter((r) => !scheduledClassroomIds.has(Number(r.classroomId)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allClassrooms, scheduledClassroomIds]
  );

  const ASSIGNMENT_BANDS = [
    { label: '9:00 AM – 11:00 AM', start: 9 * 60,  end: 11 * 60 },
    { label: '11:00 AM – 1:00 PM', start: 11 * 60, end: 13 * 60 },
    { label: '1:00 PM – 5:00 PM',  start: 13 * 60, end: 17 * 60 },
  ];

  const assignmentAllocation = useMemo(() => {
    return ASSIGNMENT_BANDS.map(({ label, start: bandStart, end: bandEnd }) => {
      const rooms = [];

      freeClassrooms.forEach((room) => {
        rooms.push({
          classroomId: room.classroomId,
          name: room.name || '—',
          floor: room.floor ?? '',
          slots: [{ start: bandStart, end: bandEnd }],
          allDay: true,
        });
      });

      occupiedClassrooms.forEach((room) => {
        const slots = room.freeSlots
          .map((s) => ({ start: Math.max(s.start, bandStart), end: Math.min(s.end, bandEnd) }))
          .filter((s) => s.end > s.start);
        if (slots.length > 0) {
          rooms.push({ classroomId: room.classroomId, name: room.name, floor: room.floor ?? '', slots, allDay: false });
        }
      });

      rooms.sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return (a.slots[0]?.start ?? 0) - (b.slots[0]?.start ?? 0);
      });

      const events = [];
      rooms.flatMap((r) => r.slots).forEach(({ start, end }) => {
        events.push([start, 1]);
        events.push([end, -1]);
      });
      events.sort((a, b) => a[0] - b[0] || b[1] - a[1]);

      let coverage = 0;
      let prev = bandStart;
      const noRoomPeriods = [];
      events.forEach(([time, delta]) => {
        if (coverage === 0 && time > prev) noRoomPeriods.push({ start: prev, end: time });
        coverage += delta;
        prev = time;
      });
      if (coverage === 0 && prev < bandEnd) noRoomPeriods.push({ start: prev, end: bandEnd });

      return { label, bandStart, bandEnd, rooms, noRoomPeriods };
    });
  }, [freeClassrooms, occupiedClassrooms]);

  // Summary numbers
  const totalBatchesOfType = useMemo(
    () => batches.filter((b) => getBatchScheduleType(b) === dayType).length,
    [batches, dayType]
  );
  const batchesWithClasses = useMemo(
    () => courseGroups.flatMap((c) => c.batches).filter((b) => b.schedules.length > 0).length,
    [courseGroups]
  );

  const buildWhatsAppMessage = () => {
    const dateLabel = formatDisplayDate(date);
    const bandEmojis = ['🕘', '🕙', '🕐'];

    const lines = [
      `📋 *ASSIGNMENT SESSION — CLASSROOM ALLOCATION*`,
      `📅 *${dateLabel}*`,
      ``,
      `Dear Students,`,
      ``,
      `Please be advised of the following venue allocation for your assignment sessions scheduled for today. Kindly take note of your designated time slot and report to the assigned venue accordingly.`,
      ``,
      `*── Venue Allocation ──*`,
      ``,
    ];

    const noRoomSlots = [];

    assignmentAllocation.forEach(({ label, rooms }, idx) => {
      const selectedId = selectedAssignmentRooms[label];
      const selectedRoom = rooms.find((r) => r.classroomId === selectedId);
      lines.push(`${bandEmojis[idx] || '🕐'} *${label}*`);
      if (rooms.length === 0) {
        lines.push(`   Venue: *Cafeteria*`);
        lines.push(`   _(No classroom available — please use the Cafeteria for this session)_`);
        noRoomSlots.push(label);
      } else if (selectedRoom) {
        lines.push(`   Venue: *${selectedRoom.name}*${selectedRoom.floor ? ` — Floor ${selectedRoom.floor}` : ''}`);
      } else {
        lines.push(`   Venue: _(To be confirmed)_`);
      }
      lines.push(``);
    });

    lines.push(`━━━━━━━━━━━━━━━━━━━━━━`);
    lines.push(``);
    lines.push(`📌 *IMPORTANT NOTICE*`);
    lines.push(``);
    lines.push(`Students are strictly required to utilise only the designated venue listed above for their assignment session. Accessing or occupying any other classroom without prior authorisation from the Academic Office constitutes a *serious disciplinary offence* and will be subject to appropriate action under the institution's disciplinary policy.`);
    lines.push(``);

    if (noRoomSlots.length > 0) {
      const slotList = noRoomSlots.map((s) => `*${s}*`).join(' and ');
      lines.push(`⚠️ Please note that no classroom is available during the ${slotList} session${noRoomSlots.length > 1 ? 's' : ''}. Students scheduled during ${noRoomSlots.length > 1 ? 'these time slots' : 'this time slot'} are required to conduct their assignment session at the *Cafeteria*.`);
      lines.push(``);
    }

    lines.push(`💡 Students who are not comfortable completing their assignment in the allocated classroom may use the *Cafeteria* as an alternative. However, students are strictly prohibited from using any other classroom that has not been officially allocated for this purpose.`);
    lines.push(``);
    lines.push(`Kindly maintain decorum and discipline within the premises at all times.`);
    lines.push(``);
    lines.push(`Thank you for your cooperation.`);
    lines.push(``);
    lines.push(`*${loggedName}*`);
    if (loggedPosition) lines.push(`_${loggedPosition}_`);
    return lines.join('\n');
  };

  const handleCopyWhatsApp = async () => {
    const msg = buildWhatsAppMessage();
    try {
      await navigator.clipboard.writeText(msg);
    } catch {
      const el = document.createElement('textarea');
      el.value = msg;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <DashboardLayout title="Daily Class Schedule">
        <div id="daily-report-print" className="min-h-screen bg-gray-50 px-6 py-8">

          {/* Print-only document header */}
          <div className="print-show">
            <div className="print-doc-header">
              <h1>Daily Class Schedule Report</h1>
              <p>{formatDisplayDate(date)}&nbsp;&nbsp;·&nbsp;&nbsp;{dayType === 'weekend' ? 'Weekend' : 'Weekday'}</p>
            </div>
          </div>

          {/* Page Header */}
          <div className="mb-6 border-b border-gray-200 pb-6 print-hide">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
                  </svg>
                </div>
                <h1 className="text-xl font-bold text-gray-900">Daily Class Schedule</h1>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${dayType === 'weekend' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                  {dayType === 'weekend' ? 'Weekend' : 'Weekday'}
                </span>
              </div>
              <p className="text-sm text-gray-500 ml-10">{formatDisplayDate(date)}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/report/monitor')}
                className="text-xs font-medium px-3 py-2 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <input
                type="date"
                value={date}
                onChange={(e) => { if (e.target.value) setDate(e.target.value); }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => window.print()}
                className="text-xs font-medium px-3 py-2 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m2 4h6a2 2 0 0 0 2-2v-4H7v4a2 2 0 0 0 2 2z" />
                </svg>
                Print
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Spinner */}
        {loading && (
          <div className="flex items-center justify-center py-32 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-3 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading report…</span>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8 print-stats-grid">
              <StatCard
                label="Batches Scheduled"
                value={batchesWithClasses}
                sub={`of ${totalBatchesOfType} ${dayType}`}
                color="green"
              />
              <StatCard
                label="No Class Today"
                value={totalBatchesOfType - batchesWithClasses}
                color={totalBatchesOfType - batchesWithClasses > 0 ? 'orange' : 'green'}
              />
              <StatCard label="Total Classes" value={schedules.length} color="blue" />
              <StatCard label="Scheduled Hours" value={`${totalHoursFor(schedules)}h`} color="indigo" />
              <StatCard label="Free Lecturers" value={Object.values(lecturerPanels).reduce((s, g) => s + g.free.length, 0)} color="purple" />
              <StatCard label="Free Classrooms" value={freeClassrooms.length} color="teal" />
            </div>

            {/* ── Course-wise Schedule ── */}
            <div className="mb-8">
              <SectionTitle
                iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                title="Course-wise Class Schedule"
                count={courseGroups.length}
              />

              {courseGroups.length === 0 ? (
                <div className="text-center py-14 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
                  No {dayType} batches found for this date.
                </div>
              ) : (
                <div className="space-y-4">
                  {courseGroups.map((course) => {
                    const courseNameLower = (course.courseName || '').toLowerCase();
                    const isHourCheckExcluded = [
                      'diploma in english',
                      'l3 diploma in it',
                      'diploma in english bundle offer',
                    ].some((ex) => courseNameLower.includes(ex));

                    return (
                    <div key={course.courseName} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-course-block">

                      {/* Course header */}
                      <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between print-course-hdr">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-800">{course.courseName}</span>
                          {course.courseShortName && (
                            <span className="text-xs text-gray-400">({course.courseShortName})</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {course.batches.length} batch{course.batches.length !== 1 ? 'es' : ''}
                        </span>
                      </div>

                      {/* Batch rows */}
                      <div className="divide-y divide-gray-100">
                        {course.batches.map((batch) => {
                          const noClass = batch.schedules.length === 0;
                          const isLowHours = !noClass && !isHourCheckExcluded && parseFloat(batch.totalHours) < 4;

                          let rowClass = 'px-5 py-3.5';
                          if (noClass) rowClass += ' bg-amber-50 border-l-4 border-l-amber-400 print-no-class';
                          else if (isLowHours) rowClass += ' bg-red-50 border-l-4 border-l-red-400 print-low-hours';

                          let nameClass = 'text-sm font-medium';
                          if (noClass) nameClass += ' text-amber-800';
                          else if (isLowHours) nameClass += ' text-red-800';
                          else nameClass += ' text-gray-700';

                          return (
                          <div key={batch.batchId} className={rowClass}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={nameClass}>
                                  {batch.batchInternalName}
                                </span>
                                {noClass ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                                    </svg>
                                    No class today
                                  </span>
                                ) : (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                    {batch.schedules.length} class{batch.schedules.length !== 1 ? 'es' : ''}
                                  </span>
                                )}
                                {isLowHours && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                                    </svg>
                                    Low hours
                                  </span>
                                )}
                              </div>
                              {batch.schedules.length > 0 && (
                                <span className={`text-xs shrink-0 font-medium ${isLowHours ? 'text-red-500' : 'text-gray-400'}`}>
                                  {batch.totalHours}h
                                </span>
                              )}
                            </div>

                            {batch.schedules.length > 0 && (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse table-fixed">
                                  <colgroup>
                                    <col style={{ width: '28%' }} />
                                    <col style={{ width: '26%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '8%' }} />
                                  </colgroup>
                                  <thead>
                                    <tr className="text-gray-400 border-b border-gray-100">
                                      <th className="text-left pb-2 pr-4 font-medium">Subject</th>
                                      <th className="text-left pb-2 pr-4 font-medium">Lecturer</th>
                                      <th className="text-left pb-2 pr-4 font-medium">Room</th>
                                      <th className="text-left pb-2 pr-2 font-medium">Time</th>
                                      <th className="text-right pb-2 font-medium">Hrs</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {batch.schedules.map((s) => (
                                      <tr key={s.scheduleId} className="border-t border-gray-50 first:border-0">
                                        <td className="py-2 pr-4 text-gray-700 font-medium truncate">
                                          <span title={s.subjectFullName || s.subjectShortName || undefined}>
                                            {s.subjectShortName || s.subjectFullName || '—'}
                                          </span>
                                        </td>
                                        <td className="py-2 pr-4 truncate">
                                          {s.staffName
                                            ? <span className="text-gray-600">{s.staffName}</span>
                                            : <span className="italic text-red-400">Unassigned</span>
                                          }
                                        </td>
                                        <td className="py-2 pr-4 truncate">
                                          {s.classroomName
                                            ? <span className="text-gray-600">{s.classroomName}</span>
                                            : <span className="italic text-orange-400">No room</span>
                                          }
                                        </td>
                                        <td className="py-2 pr-2 text-gray-500 whitespace-nowrap">
                                          {fmtTime(s.startTime)}–{fmtTime(s.endTime)}
                                        </td>
                                        <td className="py-2 text-right text-gray-400 whitespace-nowrap">
                                          {durationStr(s.startTime, s.endTime)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Lecturer + Classroom panels ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start print-avail-grid">

              {/* Staff Availability */}
              <div>
                <SectionTitle
                  iconPath="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z"
                  title="Staff Availability"
                  count={Object.values(lecturerPanels).reduce((s, g) => s + g.withClasses.length + g.free.length + g.onLeave.length + g.offDay.length, 0)}
                />

                <div className="space-y-6">
                  {['academic', 'visiting lecturer', 'sales', ...Object.keys(lecturerPanels).filter((k) => k !== 'academic' && k !== 'visiting lecturer' && k !== 'sales')]
                    .filter((k) => lecturerPanels[k])
                    .map((typeKey) => {
                      const g = lecturerPanels[typeKey];
                      const typeLabel = typeKey === 'academic' ? 'Academic' : typeKey === 'visiting lecturer' ? 'Visiting Lecturer' : typeKey === 'sales' ? 'Sales' : typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
                      const dotCls  = typeKey === 'academic' ? 'bg-indigo-500' : typeKey === 'visiting lecturer' ? 'bg-teal-500' : typeKey === 'sales' ? 'bg-purple-500' : 'bg-gray-400';
                      const hdrBg   = typeKey === 'academic' ? 'bg-indigo-50 border-indigo-200' : typeKey === 'visiting lecturer' ? 'bg-teal-50 border-teal-200' : typeKey === 'sales' ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200';
                      const hdrTxt  = typeKey === 'academic' ? 'text-indigo-700' : typeKey === 'visiting lecturer' ? 'text-teal-700' : typeKey === 'sales' ? 'text-purple-700' : 'text-gray-600';
                      const badgeCls = typeKey === 'academic' ? 'bg-indigo-100 text-indigo-700' : typeKey === 'visiting lecturer' ? 'bg-teal-100 text-teal-700' : typeKey === 'sales' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600';

                      return (
                        <div key={typeKey} className="space-y-2">
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${hdrBg} print-type-hdr`}>
                            <span className={`w-2 h-2 rounded-full ${dotCls}`} />
                            <span className={`text-xs font-bold ${hdrTxt} uppercase tracking-wide flex-1`}>{typeLabel}</span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeCls}`}>
                              {g.withClasses.length + g.free.length + g.onLeave.length + g.offDay.length}
                            </span>
                          </div>

                          <div className="space-y-2 pl-1">
                            {/* Has Classes */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">
                              <div className="px-4 py-2 bg-green-50 border-b border-green-100 flex items-center justify-between print-panel-hdr">
                                <span className="text-xs font-semibold text-green-700">Has Classes / Events Today</span>
                                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">{g.withClasses.length}</span>
                              </div>
                              {g.withClasses.length === 0 ? (
                                <p className="px-4 py-3 text-xs text-gray-400 text-center">None scheduled.</p>
                              ) : (
                                <ul className="divide-y divide-gray-100">
                                  {g.withClasses.map(({ staff, freeSlots, unavail, displaySchedules, displayEvents }) => (
                                    <li key={staff.staffId} className="px-4 py-3">
                                      <div className="flex items-center gap-2 mb-1.5">
                                        <StaffAvatar staff={staff} />
                                        <span className="text-xs font-semibold text-gray-700">{staff.name}</span>
                                        {displaySchedules.length > 0 && (
                                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">
                                            {totalHoursFor(displaySchedules)}h
                                          </span>
                                        )}
                                        {displayEvents.length > 0 && (
                                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">
                                            {displayEvents.length} event{displayEvents.length > 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {staff.roster?.dayType && staff.roster.dayType !== 'off day' && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                                            {staff.roster.dayType === 'morning half day' ? 'Morning half' : 'Evening half'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-0.5 mb-1.5">
                                        {displaySchedules.map((c, i) => (
                                          <div key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500 flex-wrap">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                            <span className="text-gray-400 tabular-nums shrink-0">{fmtTime(c.startTime)}–{fmtTime(c.endTime)}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold tabular-nums shrink-0">
                                              {durationStr(c.startTime, c.endTime)}
                                            </span>
                                            <span className="text-gray-300">·</span>
                                            <span className="font-medium text-gray-600 truncate">{c.subjectName || '—'}</span>
                                            {c.classroom && (
                                              <><span className="text-gray-300">·</span><span className="truncate">{c.classroom}</span></>
                                            )}
                                          </div>
                                        ))}
                                        {displayEvents.map((e, i) => (
                                          <div key={`ev${i}`} className="flex items-center gap-1.5 text-[10px] text-violet-600 flex-wrap">
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                                            <span className="tabular-nums shrink-0">{fmtTime(e.startTime)}–{fmtTime(e.endTime)}</span>
                                            <span className="text-violet-300">·</span>
                                            <span className="font-medium truncate">{e.title || 'Event'}</span>
                                            {e.eventTypeName && <><span className="text-violet-300">·</span><span className="truncate">{e.eventTypeName}</span></>}
                                            {e.classroom && <><span className="text-violet-300">·</span><span className="truncate">{e.classroom}</span></>}
                                          </div>
                                        ))}
                                        {unavail.filter((u) => u.type === 'unavailability').map((u, i) => (
                                          <div key={`u${i}`} className="flex items-center gap-1.5 text-[10px] text-red-500 flex-wrap">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                            <span className="tabular-nums shrink-0">{fmtTime(u.startTime)}–{fmtTime(u.endTime)}</span>
                                            <span className="font-medium">Unavailable</span>
                                            {u.reason && <><span className="text-red-200">·</span><span>{u.reason}</span></>}
                                          </div>
                                        ))}
                                      </div>
                                      {freeSlots.length > 0 && (
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex flex-wrap gap-1 items-center">
                                            <span className="text-[10px] text-gray-400">Free:</span>
                                            {freeSlots.map((slot, i) => (
                                              <span key={i} className="inline-flex items-center gap-1">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium tabular-nums">
                                                  {minsToDisplay(slot.start)}–{minsToDisplay(slot.end)}
                                                </span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                                  {slotDur(slot)}
                                                </span>
                                              </span>
                                            ))}
                                          </div>
                                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold shrink-0 whitespace-nowrap">
                                            {totalSlotDur(freeSlots)}
                                          </span>
                                        </div>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            {/* No Classes Assigned */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">
                              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between print-panel-hdr">
                                <span className="text-xs font-semibold text-blue-700">No Classes Assigned</span>
                                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">{g.free.length}</span>
                              </div>
                              {g.free.length === 0 ? (
                                <p className="px-4 py-3 text-xs text-gray-400 text-center">All have classes assigned.</p>
                              ) : (
                                <ul className="divide-y divide-gray-100">
                                  {g.free.map(({ staff, freeSlots, unavail }) => (
                                    <li key={staff.staffId} className="px-4 py-3">
                                      <div className="flex items-center gap-2 mb-1.5">
                                        <StaffAvatar staff={staff} />
                                        <span className="text-xs font-semibold text-gray-700">{staff.name}</span>
                                        {staff.roster?.dayType && staff.roster.dayType !== 'off day' && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                                            {staff.roster.dayType === 'morning half day' ? 'Morning half' : 'Evening half'}
                                          </span>
                                        )}
                                      </div>
                                      {unavail.filter((u) => u.type === 'unavailability').map((u, i) => (
                                        <div key={i} className="flex items-center gap-1.5 text-[10px] text-red-500 flex-wrap mb-1">
                                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                          <span className="tabular-nums shrink-0">{fmtTime(u.startTime)}–{fmtTime(u.endTime)}</span>
                                          <span className="font-medium">Unavailable</span>
                                          {u.reason && <><span className="text-red-200">·</span><span>{u.reason}</span></>}
                                        </div>
                                      ))}
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex flex-wrap gap-1 items-center">
                                          <span className="text-[10px] text-gray-400">Free:</span>
                                          {freeSlots.map((slot, i) => (
                                            <span key={i} className="inline-flex items-center gap-1">
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium tabular-nums">
                                                {minsToDisplay(slot.start)}–{minsToDisplay(slot.end)}
                                              </span>
                                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                                {slotDur(slot)}
                                              </span>
                                            </span>
                                          ))}
                                        </div>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold shrink-0 whitespace-nowrap">
                                          {totalSlotDur(freeSlots)}
                                        </span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            {/* On Leave */}
                            {g.onLeave.length > 0 && (
                              <div className="bg-white rounded-xl border border-orange-100 shadow-sm overflow-hidden print-panel">
                                <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 flex items-center justify-between print-panel-hdr">
                                  <span className="text-xs font-semibold text-orange-700">On Leave</span>
                                  <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">{g.onLeave.length}</span>
                                </div>
                                <ul className="divide-y divide-gray-100">
                                  {g.onLeave.map(({ staff, unavail }) => (
                                    <li key={staff.staffId} className="px-4 py-2.5 flex items-center gap-3">
                                      <StaffAvatar staff={staff} />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-gray-700">{staff.name}</div>
                                        <div className="text-[10px] text-gray-500 mt-0.5">
                                          {unavail.filter((u) => u.type === 'leave').map((u, i) => (
                                            <span key={i}>Full day leave{u.reason ? ` · ${u.reason}` : ''}</span>
                                          ))}
                                        </div>
                                      </div>
                                      <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-600">Leave</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Off Day */}
                            {g.offDay.length > 0 && (
                              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">
                                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between print-panel-hdr">
                                  <span className="text-xs font-semibold text-gray-600">Off Day</span>
                                  <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{g.offDay.length}</span>
                                </div>
                                <div className="px-4 py-3 flex flex-wrap gap-1.5">
                                  {g.offDay.map((staff) => (
                                    <span key={staff.staffId} className="text-[10px] font-medium px-2 py-1 rounded-full bg-red-50 text-red-600">
                                      {staff.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Classroom Availability */}
              <div>
                <SectionTitle
                  iconPath="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4"
                  title="Classroom Availability"
                />

                <div className="space-y-3">
                  {/* Occupied */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">
                    <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 flex items-center justify-between print-panel-hdr">
                      <span className="text-xs font-semibold text-red-700">In Use Today</span>
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                        {occupiedClassrooms.length}
                      </span>
                    </div>
                    {occupiedClassrooms.length === 0 ? (
                      <p className="px-4 py-4 text-xs text-gray-400 text-center">No classrooms scheduled.</p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {occupiedClassrooms.map((room) => {
                          const totalFreeHours = (room.freeSlots.reduce((sum, s) => sum + Math.max(0, s.end - s.start), 0) / 60).toFixed(1);
                          return (
                          <li key={room.classroomId} className="px-4 py-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-xs font-semibold text-gray-700">{room.name}</span>
                              {room.floor && (
                                <span className="text-[10px] text-gray-400">Floor {room.floor}</span>
                              )}
                              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600">
                                  {room.totalHours}h busy
                                </span>
                                {room.freeSlots.length > 0 && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-600">
                                    {totalFreeHours}h free
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="space-y-0.5 mb-1.5">
                              {room.displaySessions.map((s, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500 flex-wrap">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                  <span className="text-gray-400 tabular-nums shrink-0">
                                    {fmtTime(s.startTime)}–{fmtTime(s.endTime)}
                                  </span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold tabular-nums shrink-0">
                                    {durationStr(s.startTime, s.endTime)}
                                  </span>
                                  <span className="text-gray-300">·</span>
                                  <span className="font-medium text-gray-600 truncate">
                                    {s.subjectShortName || s.subjectFullName || '—'}
                                  </span>
                                  {s.staffName && (
                                    <>
                                      <span className="text-gray-300">·</span>
                                      <span className="truncate">{s.staffName}</span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                            {room.freeSlots.length > 0 && (
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex flex-wrap gap-1 items-center">
                                  <span className="text-[10px] text-gray-400">Free:</span>
                                  {room.freeSlots.map((slot, i) => (
                                    <span key={i} className="inline-flex items-center gap-1">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium tabular-nums">
                                        {minsToDisplay(slot.start)}–{minsToDisplay(slot.end)}
                                      </span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                        {slotDur(slot)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold shrink-0 whitespace-nowrap">
                                  {totalSlotDur(room.freeSlots)}
                                </span>
                              </div>
                            )}
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {/* Free rooms */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">
                    <div className="px-4 py-2.5 bg-teal-50 border-b border-teal-100 flex items-center justify-between print-panel-hdr">
                      <span className="text-xs font-semibold text-teal-700">Free All Day</span>
                      <span className="text-xs font-medium text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">
                        {freeClassrooms.length}
                      </span>
                    </div>
                    {freeClassrooms.length === 0 ? (
                      <p className="px-4 py-4 text-xs text-gray-400 text-center">
                        {allClassrooms.length === 0 ? 'Classroom data unavailable.' : 'All classrooms are in use.'}
                      </p>
                    ) : (
                      <div className="px-4 py-3 flex flex-wrap gap-1.5">
                        {freeClassrooms.map((room) => (
                          <span key={room.classroomId} className="text-[10px] font-medium px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100">
                            {room.name}{room.floor ? ` · ${room.floor}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* ── Today's Assignment Allocation ── */}
            <div className="mt-8">
              <SectionTitle
                iconPath="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                title="Today's Assignment Allocation"
              />
              <p className="text-xs text-gray-400 -mt-3 mb-4 ml-8">Classroom availability for assignment sessions across three time bands</p>

              {/* ── Selected summary + WhatsApp copy ── */}
              <div className="mb-4 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-hide">

                {/* Header with copy button */}
                <div className="px-4 py-2.5 bg-violet-50 border-b border-violet-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-violet-700">Selected Classrooms</span>
                  <button
                    type="button"
                    onClick={handleCopyWhatsApp}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${copySuccess ? 'bg-emerald-500 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                  >
                    {copySuccess ? (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.121.553 4.112 1.523 5.845L0 24l6.335-1.652A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.366l-.36-.214-3.76.98 1.003-3.654-.234-.374A9.818 9.818 0 0 1 2.182 12C2.182 6.58 6.58 2.182 12 2.182S21.818 6.58 21.818 12 17.42 21.818 12 21.818z"/></svg>
                        Copy WhatsApp Message
                      </>
                    )}
                  </button>
                </div>

                {/* Per-band selected room chips */}
                <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {assignmentAllocation.map(({ label, rooms }) => {
                    const selectedId = selectedAssignmentRooms[label];
                    const selectedRoom = rooms.find((r) => r.classroomId === selectedId);
                    const noRooms = rooms.length === 0;
                    return (
                      <div key={label} className={`rounded-lg border p-3 ${noRooms ? 'border-amber-200 bg-amber-50' : selectedRoom ? 'border-violet-200 bg-violet-50' : 'border-dashed border-gray-300 bg-gray-50'}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">{label}</div>
                        {noRooms ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-base leading-none">🍽️</span>
                            <div>
                              <div className="text-xs font-semibold text-amber-700">Cafeteria</div>
                              <div className="text-[10px] text-amber-500">No classroom available</div>
                            </div>
                          </div>
                        ) : selectedRoom ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-base leading-none">🏫</span>
                            <div>
                              <div className="text-xs font-semibold text-violet-700">{selectedRoom.name}</div>
                              {selectedRoom.floor && <div className="text-[10px] text-gray-400">Floor {selectedRoom.floor}</div>}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400 italic">No room selected yet</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Punishable offence notice */}
                <div className="mx-4 mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  </svg>
                  <p className="text-[11px] text-red-700 leading-relaxed">
                    <span className="font-bold">Strict Notice: </span>
                    Using any other classroom without prior authorization is a <span className="font-bold underline">punishable offence</span>. Students must strictly adhere to the allocated venue for their assignment session. If no classroom is allocated, please proceed to the <span className="font-bold">Cafeteria</span>.
                  </p>
                </div>

              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                {assignmentAllocation.map(({ label, bandStart, bandEnd, rooms, noRoomPeriods }) => (
                  <div key={label} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print-panel">

                    {/* Band header */}
                    <div className="px-4 py-2.5 bg-violet-50 border-b border-violet-100 flex items-center justify-between print-panel-hdr">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-violet-700">{label}</span>
                        <span className="text-[10px] text-violet-400 tabular-nums">{slotDur({ start: bandStart, end: bandEnd })}</span>
                      </div>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                        {rooms.length} room{rooms.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Available rooms list */}
                    {rooms.length === 0 ? (
                      <div className="px-4 py-3 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        </svg>
                        <span className="text-xs text-red-500 font-medium">No classroom available</span>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {rooms.map((room) => {
                          const isSelected = selectedAssignmentRooms[label] === room.classroomId;
                          return (
                            <li
                              key={room.classroomId}
                              onClick={() => setSelectedAssignmentRooms((prev) => ({ ...prev, [label]: room.classroomId }))}
                              className={`px-4 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <input
                                  type="radio"
                                  name={`assignment-room-${label}`}
                                  checked={isSelected}
                                  onChange={() => setSelectedAssignmentRooms((prev) => ({ ...prev, [label]: room.classroomId }))}
                                  className="w-3.5 h-3.5 shrink-0 accent-violet-600"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className={`text-xs font-semibold ${isSelected ? 'text-violet-700' : 'text-gray-700'}`}>{room.name}</span>
                                {room.floor && <span className="text-[10px] text-gray-400">· {room.floor}</span>}
                                {room.allDay && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium ml-auto">All Day</span>
                                )}
                              </div>
                              <div className="flex items-center justify-between gap-1 pl-5">
                                <div className="flex flex-wrap gap-1 items-center">
                                  {room.slots.map((slot, i) => (
                                    <span key={i} className="inline-flex items-center gap-1">
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium tabular-nums">
                                        {minsToDisplay(slot.start)}–{minsToDisplay(slot.end)}
                                      </span>
                                      <span className="text-[10px] px-1 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                                        {slotDur(slot)}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                                {room.slots.length > 1 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold shrink-0 whitespace-nowrap">
                                    {totalSlotDur(room.slots)}
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* No-room gap warning within this band */}
                    {noRoomPeriods.length > 0 && (
                      <div className="border-t border-red-100 bg-red-50/50 px-4 py-2 space-y-1">
                        {noRoomPeriods.map((period, i) => (
                          <div key={i} className="flex items-center gap-1.5 flex-wrap">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            </svg>
                            <span className="text-[10px] tabular-nums text-red-600 font-semibold">{minsToDisplay(period.start)}–{minsToDisplay(period.end)}</span>
                            <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-600 font-semibold">{slotDur(period)}</span>
                            <span className="text-[10px] text-red-400">No room</span>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>
                ))}
              </div>

            </div>

          </>
        )}
        </div>
      </DashboardLayout>
    </>
  );
}
